import './style.scss';

import React from 'react';
import Promise from 'bluebird';
import { withRouter } from 'react-router-dom';
import Text from 'components/ui/text';
import View from 'components/ui/view';
import { url as productUrl } from 'shared/models/product';
import IsolatedScroll from 'react-isolated-scroll';
import Autosuggest from 'react-autosuggest';
import { searchV3 as search } from 'shared/models/api';
import { debounce } from 'shared/utils';
import { track } from 'shared/models/analytic';
import SearchIcon from 'images/icons/controls/search.svg?react-svg';
import { sansSerifLight, fontSizeS } from 'scy/text/styles.mod.scss';
import styles from './styles.mod.scss';

const _fetchSearchResults = query => {
  return search(query, { facets: [], hitsPerPage: 20 });
};

const fetchSearchResults = debounce(_fetchSearchResults, 150);

let _currentRequest;
const autocomplete = (query = '') => {
  if (_currentRequest && _currentRequest.isPending()) _currentRequest.cancel();
  if (query.length < 3) {
    _currentRequest = null;
    return Promise.resolve([]);
  } else {
    _currentRequest = fetchSearchResults(query);
  }
  return _currentRequest.then(res => {
    track('Products Searched', { query });
    return (res && res.products) || [];
  });
};

function renderSuggestionsContainer({ containerProps, children }) {
  const { ref, ...restContainerProps } = containerProps;
  const callRef = isolatedScroll => {
    if (isolatedScroll !== null) ref(isolatedScroll.component);
  };
  return (
    <IsolatedScroll ref={callRef} {...restContainerProps}>
      {children}
    </IsolatedScroll>
  );
}

function renderSuggestion(product, { isHighlighted }) {
  const { image, title, subtitle, price, priceDisplay, volume } = product;
  const className = `search-item ${
    isHighlighted ? 'search-item--highlighted' : ''
  }`;
  const style = image ? { backgroundImage: `url(${image})` } : {};
  return (
    <div className={className}>
      <span className="search-item-image" style={style} />
      <div className="search-item-label">
        <View margin="xxs">
          <Text
            size="lg"
            weight="med"
            className="text-overflow text-capitalize"
          >
            {title}
          </Text>
        </View>
        <div className="text-overflow">{subtitle}</div>
      </div>
      <div className="search-item-info">
        {price && (
          <View margin="xxs">
            <Text size="lg" weight="med">
              ${priceDisplay}
            </Text>
          </View>
        )}
        <div>{volume}</div>
      </div>
    </div>
  );
}

function getSuggestionValue(product) {
  return product.title;
}

function noop() {}

class SearchAutocomplete extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      value: '',
      suggestions: []
    };
    this.fetchSuggestions = this.fetchSuggestions.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleSelect = this.handleSelect.bind(this);
    this.autosuggestRef = this.autosuggestRef.bind(this);
  }

  fetchSuggestions({ value }) {
    return autocomplete(value).then(suggestions =>
      this.setState({ suggestions })
    );
  }

  handleInputChange(event, { newValue }) {
    this.setState({ value: newValue });
  }

  handleSubmit(event) {
    event.preventDefault();
    if (this.state.highlighted) {
      return this.handleSelect(event, { suggestion: this.state.highlighted });
    } else {
      // There's a bug in the history package used by react-router that prevents
      // paths containing `%` from being handled properly. See this thread:
      // https://github.com/ReactTraining/history/issues/505
      // TODO: Upgrade to history v5 when it's released so we can handle `%`s.
      const searchQuery = this.state.value.replace(/%/g, '');
      const pathname = `/search/${encodeURIComponent(searchQuery)}`;
      this.navigateTo(pathname);
    }
  }

  handleSelect(event, { suggestion }) {
    event.preventDefault();
    let pathname = productUrl(suggestion);
    if (pathname && pathname.indexOf('/') !== 0) pathname = '/' + pathname;
    if (pathname) this.navigateTo(pathname);
  }

  autosuggestRef(autosuggestElement) {
    if (autosuggestElement) {
      this.input = autosuggestElement.input;
    }
  }

  navigateTo(pathname) {
    this.props.history.push(pathname);
    this.setState({ value: '', suggestions: [] });
    if (this.input) this.input.blur();
  }

  render() {
    return (
      <div id="search" className={this.props.className}>
        <form onSubmit={this.handleSubmit}>
          <div className={styles.iconContainer}>
            <SearchIcon />
          </div>
          <Autosuggest
            ref={this.autosuggestRef}
            inputProps={{
              className: `search-query ${fontSizeS} ${sansSerifLight}`,
              placeholder: 'What can we help you find?',
              value: this.state.value,
              onChange: this.handleInputChange
            }}
            onSuggestionsFetchRequested={this.fetchSuggestions}
            onSuggestionsClearRequested={noop}
            suggestions={this.state.suggestions}
            renderSuggestionsContainer={renderSuggestionsContainer}
            renderSuggestion={renderSuggestion}
            getSuggestionValue={getSuggestionValue}
            onSuggestionSelected={this.handleSelect}
            onSuggestionHighlighted={this.handleHighlight}
            focusInputOnSuggestionClick={false}
          />
        </form>
      </div>
    );
  }
}

const SearchAutocompleteWithRouter = withRouter(SearchAutocomplete);

export default SearchAutocompleteWithRouter;
